<script lang="ts">
    import { tick } from "svelte";
    import { scale } from "svelte/transition";
    import { SpecificityTypes, specificityThreshold } from "@/Common/constants";
    import { cn } from "@/lib/utils";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";

    /**
     * The plan's specificity as a small pill. Tapping it opens the three choices as a column over it, the current one where
     * the pill was, and picking one closes it. From the keyboard the column is a menu: opening it focuses the current
     * choice, the arrow keys move between the choices, Enter or Space picks one, and Escape closes it, the focus going back
     * to the pill.
     */
    export let specificity: SpecificityTypes
    export let disabled = false
    export let onInput: () => void = () => {}
    let className = ""
    export {className as class}

    const choices = [SpecificityTypes.Polar, SpecificityTypes.Mixed, SpecificityTypes.Pure]
    // The height of a choice in the column, in pixels: the column is moved up by the choices above the current one
    const ITEM = 32
    let open = false
    let root: HTMLElement
    let pill: HTMLButtonElement
    let items: HTMLButtonElement[] = []
    // The choice with the focus in the column: the only one reached by Tab
    let focused = 0
    $: index = choices.indexOf(specificity)

    async function openChoices() {
        open = true
        focused = index
        await tick()
        items[focused]?.focus()
    }
    function close(refocus: boolean) {
        open = false
        if (refocus) pill?.focus()
    }
    function pick(choice: SpecificityTypes) {
        close(true)
        if (choice === specificity) return
        specificity = choice
        onInput()
    }
    function onItemKeydown(e: KeyboardEvent) {
        const moves: Record<string, number> = { ArrowDown: focused + 1, ArrowUp: focused - 1, Home: 0, End: choices.length - 1 }
        if (e.key in moves) {
            e.preventDefault()
            focused = (moves[e.key] + choices.length) % choices.length
            items[focused]?.focus()
        } else if (e.key === "Tab") {
            close(false)
        }
    }
    function onWindowClick(e: MouseEvent) {
        if (open && !root.contains(e.target as Node)) close(false)
    }
</script>

<!-- Escape closes it wherever the focus is (a click on the column's edge leaves it on the page) -->
<svelte:window on:click={onWindowClick} on:keydown={e => { if (open && e.key === "Escape") close(true) }}/>

<div class={cn("inline-flex items-center", className)}>
<div bind:this={root} class="relative inline-flex">
    <button bind:this={pill} type="button" {disabled} aria-haspopup="menu" aria-expanded={open} aria-label="Specificity: {specificity}, {specificityThreshold[specificity].toLowerCase()}. Change"
        title="Specificity: {specificityThreshold[specificity]}"
        class="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-sm font-medium text-slate-600 hover:border-xerteal-400 hover:text-xerteal-700 disabled:opacity-60"
        style="height: {ITEM}px" on:click={() => open ? close(false) : openChoices()}
        on:keydown={e => { if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); openChoices() } }}>
        {specificity}
        <i class="fa-solid fa-sort text-[9px] text-slate-400"/>
    </button>
    {#if open}
        <div role="menu" aria-label="Specificity"
            class="absolute left-1/2 z-20 flex flex-col p-1 rounded-2xl border border-slate-300 bg-white shadow-lg"
            style="top: {-index*ITEM - 5}px; translate: -50% 0; transform-origin: 50% {index*ITEM + ITEM/2 + 4}px"
            transition:scale={{duration: 150, start: 0.6}}>
            {#each choices as choice, i}
                <button bind:this={items[i]} type="button" role="menuitemradio" aria-checked={choice === specificity} title={specificityThreshold[choice]}
                    tabindex={i === focused ? 0 : -1} on:keydown={onItemKeydown}
                    class={cn("min-w-[4.5rem] px-3 rounded-xl border-0 text-sm text-center whitespace-nowrap transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-xerteal-400",
                        choice === specificity ? "bg-xerteal-50 font-semibold text-xerteal-700" : "bg-transparent text-slate-600 hover:bg-slate-100")}
                    style="height: {ITEM}px" on:click={() => pick(choice)}>
                    {choice}
                </button>
            {/each}
        </div>
    {/if}
</div>
<HelpTooltip label="What is specificity?" iconClass="text-gray-500 -mr-1"
    message="Specificity describes how much High Intensity Energy contributes to your focus power. <b>Polar</b> minimizes it, making Threshold and Peak Power absorb the training. <b>Pure</b> maximizes it, raising HIE the highest. <b>Mixed</b> balances it, aligning with how it is used most commonly on spirited rides and races."/>
</div>
